import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Wrench, LogOut, User, MessageSquare, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const NavItem = ({ to, children, testid, onClick }) => {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== "/" && pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${
        active ? "text-[#FF5100]" : "text-neutral-700 hover:text-neutral-900"
      }`}
    >
      {children}
    </Link>
  );
};

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [waConfig, setWaConfig] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setWaConfig(r.data)).catch(() => {});
  }, []);

  const onLogout = () => {
    logout();
    setOpen(false);
    nav("/");
  };

  const close = () => setOpen(false);

  const waLink = (() => {
    if (!waConfig?.whatsapp_number) return null;
    const digits = waConfig.whatsapp_number.replace(/\D/g, "");
    if (!digits) return null;
    const text = waConfig.whatsapp_join_code
      ? waConfig.whatsapp_join_code
      : "Hi Spannr, I need a part for my vehicle";
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
  })();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-neutral-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" onClick={close} className="flex items-center gap-2 group">
          <div className="w-8 h-8 grid place-items-center bg-[#FF5100] text-black">
            <Wrench size={18} strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-xl font-extrabold tracking-tight text-neutral-900">
              SPANNR<span className="text-[#FF5100]">.</span>
            </div>
            <div className="font-mono-tech text-[10px] text-neutral-500 -mt-0.5">
              SPANNR.CO.ZA · SA PARTS AI
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          <NavItem to="/" testid="nav-chat">Chat</NavItem>
          <NavItem to="/browse" testid="nav-browse">Browse</NavItem>
          <NavItem to="/pricing" testid="nav-pricing">Pricing</NavItem>

          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp"
              title="Chat with Spannr on WhatsApp"
              className="flex items-center gap-1.5 ml-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#25D366] hover:bg-[#1EBE5D] text-black transition-colors"
            >
              <MessageSquare size={12} />
              <span>WhatsApp</span>
            </a>
          )}

          {user ? (
            <>
              {user.role === "admin" ? (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className="px-3 py-2 text-sm font-medium tracking-wide uppercase text-[#FF5100] hover:text-neutral-900"
                >
                  Admin
                </Link>
              ) : (
                <Link
                  to={`/suppliers/dashboard/${user.supplier_id}`}
                  data-testid="nav-dashboard"
                  className="px-3 py-2 text-sm font-medium tracking-wide uppercase text-neutral-700 hover:text-neutral-900"
                >
                  Dashboard
                </Link>
              )}
              <div className="hidden lg:flex items-center gap-1.5 px-3 text-xs text-neutral-500 border-l border-neutral-200 ml-1">
                <User size={12} />
                <span data-testid="header-user-email" className="truncate max-w-[140px]">{user.email}</span>
              </div>
              <button
                data-testid="header-logout"
                onClick={onLogout}
                title="Sign out"
                className="px-3 py-2 text-neutral-500 hover:text-[#FF5100] transition-colors"
              >
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <>
              <NavItem to="/suppliers/register" testid="nav-supplier">Suppliers</NavItem>
              <Link
                to="/login"
                data-testid="nav-login"
                className="ml-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#FF5100] hover:bg-[#E64A00] text-white transition-colors"
              >
                Sign in
              </Link>
            </>
          )}
        </nav>

        {/* Mobile burger */}
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          className="md:hidden p-2 -mr-2 text-neutral-700 hover:text-[#FF5100] transition-colors"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="md:hidden border-t border-neutral-200 bg-white"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
            <NavItem to="/" testid="mobile-nav-chat" onClick={close}>Chat</NavItem>
            <NavItem to="/browse" testid="mobile-nav-browse" onClick={close}>Browse</NavItem>
            <NavItem to="/pricing" testid="mobile-nav-pricing" onClick={close}>Pricing</NavItem>

            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-whatsapp"
                onClick={close}
                className="mt-2 flex items-center gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-wider bg-[#25D366] hover:bg-[#1EBE5D] text-black transition-colors"
              >
                <MessageSquare size={14} /> Chat on WhatsApp
              </a>
            )}

            <div className="mt-2 border-t border-neutral-200" />

            {user ? (
              <>
                {user.role === "admin" ? (
                  <NavItem
                    to="/admin"
                    testid="mobile-nav-admin"
                    onClick={close}
                  >
                    Admin
                  </NavItem>
                ) : (
                  <NavItem
                    to={`/suppliers/dashboard/${user.supplier_id}`}
                    testid="mobile-nav-dashboard"
                    onClick={close}
                  >
                    Dashboard
                  </NavItem>
                )}
                <div className="px-3 py-2 text-xs text-neutral-500 flex items-center gap-1.5">
                  <User size={12} />
                  <span data-testid="mobile-header-user-email" className="truncate">{user.email}</span>
                </div>
                <button
                  data-testid="mobile-header-logout"
                  onClick={onLogout}
                  className="mx-3 mt-1 mb-2 flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-neutral-200 hover:border-neutral-300 text-neutral-700 transition-colors"
                >
                  <LogOut size={12} /> Sign out
                </button>
              </>
            ) : (
              <>
                <NavItem to="/suppliers/register" testid="mobile-nav-supplier" onClick={close}>
                  Suppliers
                </NavItem>
                <Link
                  to="/login"
                  data-testid="mobile-nav-login"
                  onClick={close}
                  className="mt-2 mx-3 px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wider bg-[#FF5100] hover:bg-[#E64A00] text-white transition-colors"
                >
                  Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
